import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signInWithCustomToken
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase/config";
import { CONSENT_TEXT } from "../../lib/compliance";
import PdfViewer from "../../components/PdfViewer";
import SignaturePad from "../../components/SignaturePad";
import TypedSignature from "../../components/TypedSignature";

const STEP = {
  LOADING: "loading",
  OTP_REQUEST: "otp_request",
  OTP_VERIFY: "otp_verify",
  CONSENT: "consent",
  SIGN: "sign",
  SUBMITTING: "submitting",
  DONE: "done"
};

export default function Sign() {
  const { signerToken } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.LOADING);
  const [session, setSession] = useState(null); // { title, pdfUrl, verificationMode, ... }
  const [confirmation, setConfirmation] = useState(null);
  const [code, setCode] = useState("");
  const [consent, setConsent] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [padOpen, setPadOpen] = useState(false);
  const [typedOpen, setTypedOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [savedSignature, setSavedSignature] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const beginSigning = httpsCallable(functions, "beginSigning");
        const { data } = await beginSigning({ signerToken });
        setSession(data);
        if (data.verificationMode === "email_only") {
          // No SMS step — exchange the token straight away for a scoped session.
          try {
            const exchange = httpsCallable(functions, "exchangeSignerToken");
            const { data: ex } = await exchange({ signerToken });
            await signInWithCustomToken(auth, ex.customToken);
            setStep(STEP.CONSENT);
          } catch (err) {
            setError(err.message || "Could not start signing.");
          }
        } else {
          setStep(STEP.OTP_REQUEST);
        }
      } catch (err) {
        setError(err.message || "This signing link is invalid or expired.");
      }
    })();
  }, [signerToken]);

  const sendCode = async () => {
    setError("");
    setSending(true);
    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, "recaptcha-holder", {
          size: "invisible"
        });
      }
      const conf = await signInWithPhoneNumber(
        auth,
        session.phoneE164,
        window.recaptchaVerifier
      );
      setConfirmation(conf);
      setStep(STEP.OTP_VERIFY);
    } catch (err) {
      setError(err.message || "Could not send code.");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    setError("");
    setVerifying(true);
    try {
      await confirmation.confirm(code);
      const exchange = httpsCallable(functions, "exchangeSignerToken");
      const { data } = await exchange({ signerToken });
      await signInWithCustomToken(auth, data.customToken);
      setStep(STEP.CONSENT);
    } catch (err) {
      setError(err.message || "Incorrect code.");
    } finally {
      setVerifying(false);
    }
  };

  const submit = async () => {
    setError("");
    if (!signatureDataUrl) return setError("Please draw your signature.");
    if (!typedName.trim())
      return setError("Please type your legal name as it appears on the document.");
    setStep(STEP.SUBMITTING);
    try {
      const submitSignature = httpsCallable(functions, "submitSignature");
      await submitSignature({
        signerToken,
        signatureDataUrl,
        typedName: typedName.trim(),
        consentAcceptedAt: new Date().toISOString(),
        userAgent: navigator.userAgent
      });
      navigate(`/sign/${signerToken}/done`);
    } catch (err) {
      setError(err.message || "Could not submit signature.");
      setStep(STEP.SIGN);
    }
  };

  if (error && !session) {
    return <div className="mx-auto max-w-md p-6 text-red-700">{error}</div>;
  }

  return (
    <div className="mx-auto max-w-2xl p-4">
      <div id="recaptcha-holder" />

      {step === STEP.LOADING && <div className="p-6 text-slate-500">Loading…</div>}

      {step === STEP.OTP_REQUEST && session && (
        <div className="card space-y-3">
          <h1 className="text-xl font-semibold">{session.title}</h1>
          <p className="text-sm text-slate-600">
            To confirm it's you, we'll send a 6-digit code to{" "}
            <strong>{session.phoneMasked}</strong>.
          </p>
          <button
            className="btn-primary w-full"
            onClick={sendCode}
            disabled={sending}
          >
            {sending ? "Sending…" : "Send code"}
          </button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {step === STEP.OTP_VERIFY && (
        <div className="card space-y-3">
          <h1 className="text-xl font-semibold">Enter the code</h1>
          <p className="text-sm text-slate-600">
            Check your phone for a text message.
          </p>
          <input
            className="input text-center text-2xl tracking-widest"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            disabled={verifying}
          />
          <button
            className="btn-primary w-full"
            onClick={verifyCode}
            disabled={verifying || code.length < 6}
          >
            {verifying ? "Verifying…" : "Verify"}
          </button>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}

      {step === STEP.CONSENT && (
        <div className="card space-y-3">
          <h1 className="text-xl font-semibold">Consent to sign electronically</h1>
          <p className="text-sm text-slate-700">{CONSENT_TEXT}</p>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
            />
            <span>I agree.</span>
          </label>
          <button
            className="btn-primary w-full"
            disabled={!consent}
            onClick={() => setStep(STEP.SIGN)}
          >
            Continue
          </button>
        </div>
      )}

      {step === STEP.SIGN && session && (() => {
        const nameDone = typedName.trim().length > 0;
        const signatureDone = !!signatureDataUrl;
        const savedDone = savedSignature;
        const stepState = (done, enabled) =>
          done ? "done" : enabled ? "active" : "pending";
        const s1 = stepState(nameDone, true);
        const s2 = stepState(signatureDone, nameDone);
        const s3 = stepState(savedDone, signatureDone);
        const s4 = stepState(false, savedDone);
        const badge = (n, state) => (
          <span
            className={
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold " +
              (state === "done"
                ? "bg-emerald-500 text-white"
                : state === "active"
                ? "bg-brand-500 text-white"
                : "bg-slate-200 text-slate-500")
            }
          >
            {state === "done" ? "✓" : n}
          </span>
        );
        const titleClass = (state) =>
          "text-sm font-medium " +
          (state === "pending" ? "text-slate-400" : "text-slate-800");
        return (
        <div className="space-y-4">
          <div className="card space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {badge(1, s1)}
                <span className={titleClass(s1)}>Type your legal name</span>
              </div>
              <input
                className="input"
                value={typedName}
                onChange={(e) => {
                  setTypedName(e.target.value);
                  setSavedSignature(false);
                }}
                placeholder="As it appears on the document"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {badge(2, s2)}
                <span className={titleClass(s2)}>Your signature</span>
              </div>
              {signatureDataUrl ? (
                <div className="rounded-lg border border-slate-200 p-2">
                  <img
                    src={signatureDataUrl}
                    alt="signature preview"
                    className="h-24 w-full object-contain"
                  />
                  <div className="mt-2 flex gap-2">
                    <button
                      className="btn-secondary text-sm"
                      onClick={() => setPadOpen(true)}
                    >
                      Redraw
                    </button>
                    <button
                      className="btn-secondary text-sm"
                      onClick={() => setTypedOpen(true)}
                    >
                      Type instead
                    </button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className={
                      nameDone
                        ? "btn-primary"
                        : "btn-secondary cursor-not-allowed opacity-50"
                    }
                    onClick={() => setPadOpen(true)}
                    disabled={!nameDone}
                  >
                    Draw
                  </button>
                  <button
                    className={
                      nameDone
                        ? "btn-primary"
                        : "btn-secondary cursor-not-allowed opacity-50"
                    }
                    onClick={() => setTypedOpen(true)}
                    disabled={!nameDone}
                  >
                    Type
                  </button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3">
                {badge(3, s3)}
                <span className={titleClass(s3)}>Save your signature</span>
              </div>
              <button
                className={
                  "w-full " +
                  (signatureDone && !savedSignature
                    ? "btn-primary"
                    : savedSignature
                    ? "btn-secondary cursor-not-allowed opacity-70"
                    : "btn-secondary cursor-not-allowed opacity-50")
                }
                onClick={() => {
                  setError("");
                  if (!typedName.trim())
                    return setError(
                      "Please type your legal name as it appears on the document."
                    );
                  if (!signatureDataUrl)
                    return setError("Please draw or type your signature.");
                  setSavedSignature(true);
                }}
                disabled={!signatureDone || savedSignature}
              >
                {savedSignature ? "Saved ✓" : "Save signature"}
              </button>
            </div>

          </div>
          <div className="card space-y-3">
            <div className="flex items-center gap-3">
              {badge(4, s4)}
              <span className={titleClass(s4)}>Submit document</span>
            </div>
            <p className={`text-sm ${savedSignature ? "text-slate-600" : "text-slate-400"}`}>
              {savedSignature
                ? "Review the document below, then submit to finalise your signature."
                : "Complete the steps above before submitting."}
            </p>
            <button
              className={
                "w-full " +
                (savedSignature ? "btn-primary" : "btn-secondary cursor-not-allowed opacity-50")
              }
              onClick={submit}
              disabled={!savedSignature || step === STEP.SUBMITTING}
            >
              Submit document
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <div className="card">
            <h2 className="mb-1 font-semibold">Review the document</h2>
            <p className="mb-3 text-xs text-slate-500">
              {savedSignature
                ? "Your signature is shown where it will appear on the final PDF."
                : "Save your signature above to see it placed on the document."}
            </p>
            <PdfViewer
              file={session.pdfUrl}
              overlay={(pageNumber) => {
                if (!savedSignature) return null;
                const dateStr = new Date().toLocaleDateString("en-NZ", {
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                });
                return (
                  <div className="pointer-events-none absolute inset-0">
                    {(session.fields || [])
                      .filter((f) => f.page === pageNumber)
                      .map((f, i) => (
                        <div
                          key={i}
                          className="absolute"
                          style={{
                            left: `${f.x * 100}%`,
                            top: `${f.y * 100}%`,
                            width: `${f.w * 100}%`,
                            height: `${f.h * 100}%`
                          }}
                        >
                          {f.type === "date" ? (
                            <div className="flex h-full items-center text-[clamp(8px,1.6vw,14px)] text-slate-900">
                              {dateStr}
                            </div>
                          ) : (
                            <img
                              src={signatureDataUrl}
                              alt="Your signature"
                              className="h-full w-full object-contain"
                            />
                          )}
                        </div>
                      ))}
                  </div>
                );
              }}
            />
          </div>
          <div className="card space-y-3">
            <div className="flex items-center gap-3">
              {badge(4, s4)}
              <span className={titleClass(s4)}>Submit document</span>
            </div>
            <p className={`text-sm ${savedSignature ? "text-slate-600" : "text-slate-400"}`}>
              {savedSignature
                ? "Review the document above, then submit to finalise your signature."
                : "Complete the steps above before submitting."}
            </p>
            <button
              className={
                "w-full " +
                (savedSignature ? "btn-primary" : "btn-secondary cursor-not-allowed opacity-50")
              }
              onClick={submit}
              disabled={!savedSignature || step === STEP.SUBMITTING}
            >
              Submit document
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <SignaturePad
            open={padOpen}
            onCancel={() => setPadOpen(false)}
            onAccept={(dataUrl) => {
              setSignatureDataUrl(dataUrl);
              setSavedSignature(false);
              setPadOpen(false);
            }}
          />
          <TypedSignature
            open={typedOpen}
            defaultName={typedName}
            onCancel={() => setTypedOpen(false)}
            onAccept={(dataUrl) => {
              setSignatureDataUrl(dataUrl);
              setSavedSignature(false);
              setTypedOpen(false);
            }}
          />
        </div>
        );
      })()}

      {step === STEP.SUBMITTING && (
        <div className="p-6 text-slate-500">Submitting…</div>
      )}
    </div>
  );
}
